import {
  type InfiniteData,
  infiniteQueryOptions,
  useQueryClient,
} from '@tanstack/react-query';
import * as React from 'react';
import { type StaticDecode, Type } from 'typebox';
import * as TypeCompiler from 'typebox/compile';
import _ from 'underscore';
import type { Masterdata } from 'schema/masterdata';
import {
  ReadonlyArray,
  ReadonlyInterface,
  ReadonlyObject,
} from 'schema/schema';
import { invalidateUnapprovedCount } from 'stores/Counts';
import { httpChecked, queryClient } from 'util/fetch';
import { invariant } from 'util/invariant';
import { useDebouncedInfiniteQuery } from 'util/query';

const termSchema = ReadonlyObject({
  term: Type.String(),
  count: Type.Integer(),
});

const schema = ReadonlyObject({
  wrapper: ReadonlyObject({
    terms: ReadonlyArray(
      ReadonlyInterface([termSchema], {
        matching_categories: Type.Optional(ReadonlyArray(termSchema)),
      }),
    ),
  }),
});
const checker = TypeCompiler.Compile(schema);

type Terms = StaticDecode<typeof schema>['wrapper']['terms'];
type Term = Terms[number];
export interface UnapprovedTerm extends Term {
  // this is super weird, but we add a "masterdata" field to the term when we
  // approve it, so that we can show the masterdata in the UI
  masterdata?: Masterdata;
}

const limit = 20;

const queries = {
  field: (section_type: string, field_name: string) =>
    ['unapproved', { section_type, field_name }] as const,
  fetch: (
    section_type: string,
    field_name: string,
    language_code: string,
    options?: { category?: string; value?: string; query?: string },
  ) =>
    infiniteQueryOptions({
      queryKey: [
        ...queries.field(section_type, field_name),
        { language_code },
        options,
      ] as const,
      queryFn: async ({ signal, pageParam }) => {
        const url = new URL(
          `/api/v1/unapproved/${language_code}/${section_type}/${field_name}`,
          window.location.origin,
        );

        url.searchParams.set('limit', limit.toString());
        url.searchParams.set('offset', pageParam.toString());

        if (options?.category) {
          url.searchParams.set('category', options.category);
        }
        if (options?.value) {
          url.searchParams.set('value', options.value);
        }
        if (options?.query && options.query.length > 0) {
          url.searchParams.set('query', options.query);
        }

        const { wrapper } = await httpChecked.get(url, checker, { signal });

        void invalidateUnapprovedCount(section_type, field_name, language_code);

        return wrapper.terms;
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.length < limit) {
          return undefined;
        }

        return allPages.flat().length;
      },
    }),
};

type ReplacedTermsContext = {
  replacedTerms: readonly UnapprovedTerm[];
  recentlyApproved: (
    section_type: string,
    field_type: string,
    value: string,
    masterdata: Masterdata,
  ) => void;
  removeRecentlyApproved: (term: string) => void;
  clearReplacedTerms: () => void;
};

const ReplacedTermsContext = React.createContext<ReplacedTermsContext | null>(
  null,
);

export const ReplacedTermsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const queryClient = useQueryClient();

  const [replacedTerms, setReplacedTerms] = React.useState<
    readonly UnapprovedTerm[]
  >([]);

  const value = React.useMemo<ReplacedTermsContext>(() => {
    const recentlyApproved: ReplacedTermsContext['recentlyApproved'] = (
      section_type,
      field_type,
      value,
      masterdata,
    ) => {
      const queryKey = queries.field(section_type, field_type);

      const query = queryClient
        .getQueryCache()
        .find({ queryKey, type: 'active', exact: false });

      if (query == null) {
        void queryClient.invalidateQueries({ queryKey });
        return;
      }

      const exactQueryKey = query.queryKey;

      const queryData =
        queryClient.getQueryData<InfiniteData<Terms>>(exactQueryKey)!;

      const [recentlyApprovedTerms, oldTerms] = _.partition(
        queryData.pages.flat(),
        (term) => term.term === value,
      );

      if (recentlyApprovedTerms.length === 0) {
        void queryClient.invalidateQueries({ queryKey });
        return;
      }

      const newPages: Terms[] = [];

      if (oldTerms.length > 0) {
        while (oldTerms.length > 0) {
          const newPage = oldTerms.splice(0, limit);
          newPages.push(newPage);
        }
      } else {
        newPages.push([]);
      }

      queryClient.setQueryData<InfiniteData<Terms>>(exactQueryKey, {
        pageParams: queryData.pageParams.slice(0, newPages.length),
        pages: newPages,
      });

      setReplacedTerms((replacedTerms) => [
        ...replacedTerms,
        ...recentlyApprovedTerms.map((term) => ({ ...term, masterdata })),
      ]);

      void queryClient.invalidateQueries({ queryKey });
    };

    const removeRecentlyApproved: ReplacedTermsContext['removeRecentlyApproved'] =
      (term) => {
        setReplacedTerms((replacedTerms) => {
          const newReplacedTerms = replacedTerms.filter((t) => t.term !== term);

          if (newReplacedTerms.length === replacedTerms.length) {
            return replacedTerms;
          }

          return newReplacedTerms;
        });
      };

    const clearReplacedTerms: ReplacedTermsContext['clearReplacedTerms'] =
      () => {
        setReplacedTerms([]);
      };

    return {
      replacedTerms,
      recentlyApproved,
      removeRecentlyApproved,
      clearReplacedTerms,
    };
  }, [queryClient, replacedTerms]);

  return (
    <ReplacedTermsContext.Provider value={value}>
      {children}
    </ReplacedTermsContext.Provider>
  );
};

function useReplacedTermsContext() {
  const context = React.useContext(ReplacedTermsContext);

  invariant(
    context,
    'useReplacedTermsContext must be used within a ReplacedTermsProvider',
  );

  return context;
}

export function useRecentlyApproved() {
  const { recentlyApproved } = useReplacedTermsContext();

  return recentlyApproved;
}

export function useRemoveRecentlyApproved() {
  const { removeRecentlyApproved } = useReplacedTermsContext();

  return removeRecentlyApproved;
}

export function useUnapproved(
  section_type: string,
  field_name: string,
  language_code: string,
  search_term = '',
) {
  const {
    query: { data, fetchNextPage, hasNextPage = false, isFetchingNextPage },
    queryKeyHash,
  } = useDebouncedInfiniteQuery(
    (search_term) =>
      infiniteQueryOptions({
        ...queries.fetch(
          section_type,
          field_name,
          language_code,
          search_term ? { query: search_term } : undefined,
        ),
        select: (data) => data.pages.flat(),
        staleTime: 60_000,
      }),
    search_term,
  );
  const { replacedTerms, clearReplacedTerms } = useReplacedTermsContext();

  React.useEffect(() => {
    // clear the cache when the query key changes
    clearReplacedTerms();
  }, [clearReplacedTerms, queryKeyHash]);

  let terms = data;

  if (terms != null) {
    let uniqueTerms = Array.from(
      terms
        .reduce(
          (map, term) => map.set(term.term, term),
          new Map<string, Term>(),
        )
        .values(),
    );

    if (replacedTerms.length > 0) {
      const replacedTermsValues = new Set(
        replacedTerms.map((term) => term.term),
      );

      uniqueTerms = uniqueTerms
        .filter((unapproved) => !replacedTermsValues.has(unapproved.term))
        .concat(replacedTerms);
    }

    terms = uniqueTerms.sort((a, b) => {
      const count_compare = b.count - a.count;

      if (count_compare !== 0) {
        return count_compare;
      }

      return a.term.localeCompare(b.term);
    });
  }

  return {
    data: terms,
    fetchNextPage: async () => {
      if (isFetchingNextPage) {
        return;
      }
      await fetchNextPage();
    },
    hasNextPage,
  };
}

export async function invalidateUnapproved(
  section_type: string,
  field_name: string,
) {
  await queryClient.invalidateQueries({
    queryKey: queries.field(section_type, field_name),
  });
}
